import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import CardHeader from "../../components/common/CardHeader";
import { downloadReport, downloadDeferredReport } from "../../services/Report";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ReportTypeConstants } from "../../constants";
import config from "../../config";
import { buildMonthOptions } from "../../utils/utils";


import logger from "../../utils/logger";
const fmt = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const firstDayOfMonth = (y, m /* 1-12 */) => new Date(y, m - 1, 1);
const lastDayOfMonth = (y, m /* 1-12 */) => new Date(y, m, 0);

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const presets = [
  {
    key: "thisMonth",
    label: "Bieżący miesiąc",
    get: () => {
      const now = new Date();
      const from = firstDayOfMonth(now.getFullYear(), now.getMonth() + 1);
      const to = lastDayOfMonth(now.getFullYear(), now.getMonth() + 1);
      return { from, to };
    },
  },
  {
    key: "lastMonth",
    label: "Poprzedni miesiąc",
    get: () => {
      const now = new Date();
      const y =
        now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const m = now.getMonth() === 0 ? 12 : now.getMonth();
      return { from: firstDayOfMonth(y, m), to: lastDayOfMonth(y, m) };
    },
  },
  {
    key: "last7",
    label: "Ostatnie 7 dni",
    get: () => {
      const to = new Date();
      const from = addDays(to, -6);
      return { from, to };
    },
  },
  {
    key: "last30",
    label: "Ostatnie 30 dni",
    get: () => {
      const to = new Date();
      const from = addDays(to, -29);
      return { from, to };
    },
  },
  {
    key: "ytd",
    label: "Rok bieżący (YTD)",
    get: () => {
      const now = new Date();
      return { from: new Date(now.getFullYear(), 0, 1), to: now };
    },
  },
];

/**
 * Komponent wyboru okresu – używany TYLKO w "Miesięczny raport odroczonych należności".
 * Tryby: predefiniowany miesiąc LUB niestandardowy zakres dat.
 */
function PeriodSelector({ value, onChange, monthOptions }) {
  const [mode, setMode] = useState(value?.mode ?? "month"); // "month" | "custom"
  const [monthValue, setMonthValue] = useState(
    value?.month ?? monthOptions[0]?.value ?? ""
  );
  const [from, setFrom] = useState(value?.from ?? null);
  const [to, setTo] = useState(value?.to ?? null);

  useEffect(() => {
    if (mode === "month" && monthValue) {
      const [y, m] = monthValue.split("-").map(Number);
      onChange?.({
        mode,
        month: monthValue,
        from: firstDayOfMonth(y, m),
        to: lastDayOfMonth(y, m),
      });
    } else if (mode === "custom") {
      onChange?.({ mode, month: null, from, to });
    }
  }, [mode, monthValue, from, to]);

  const applyPreset = (p) => {
    const { from: f, to: t } = p.get();
    setMode("custom");
    setFrom(f);
    setTo(t);
  };

  return (
    <div className="d-flex flex-column gap-2">
      <div className="btn-group" role="group" aria-label="Wybór trybu zakresu">
        <button
          type="button"
          className={`btn ${
            mode === "month" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setMode("month")}
        >
          Miesiąc
        </button>
        <button
          type="button"
          className={`btn ${
            mode === "custom" ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => setMode("custom")}
        >
          Zakres niestandardowy
        </button>
      </div>

      {mode === "month" && (
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select"
            value={monthValue}
            onChange={(e) => setMonthValue(e.target.value)}
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === "custom" && (
        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center gap-2">
            <DatePicker
              selected={from}
              onChange={setFrom}
              selectsStart
              startDate={from}
              endDate={to}
              locale="pl"
              placeholderText="Od"
              dateFormat="yyyy-MM-dd"
              className="form-control"
            />
            <span className="text-muted">–</span>
            <DatePicker
              selected={to}
              onChange={setTo}
              selectsEnd
              startDate={from}
              endDate={to}
              minDate={from}
              locale="pl"
              placeholderText="Do"
              dateFormat="yyyy-MM-dd"
              className="form-control"
            />
          </div>

          <div className="d-flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReportGenerator() {
  const { checkPermission, accessToken } = useAuth();

  const canBasic = checkPermission("Raport podstawowy");
  const canExtended = checkPermission("Raport zaawansowany");
  const canDeferred = checkPermission("Raport odroczonych należności");

  const [selectedReport, setSelectedReport] = useState(() => {
    if (canBasic) return ReportTypeConstants.BASIC;
    if (canExtended) return ReportTypeConstants.EXTENDED;
    return null;
  });

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [isReportSubmitting, setIsReportSubmitting] = useState(false);

  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  const [deferredPeriod, setDeferredPeriod] = useState({
    mode: "month",
    month: monthOptions[0]?.value ?? "",
    from: null,
    to: null,
  });
  const [deferredLoading, setDeferredLoading] = useState(false);

  useEffect(() => {
    if (monthOptions[0]) {
      setDeferredPeriod((prev) => ({
        ...prev,
        mode: "month",
        month: monthOptions[0].value,
      }));
    }
  }, [monthOptions.length]);

  const handleGenerate = () => {
    setIsReportSubmitting(true);
    if (!selectedReport) {
      alert("Wybierz rodzaj raportu");
      setIsReportSubmitting(false);
      return;
    }
    if (!dateFrom || !dateTo) {
      alert("Podaj zakres dat");
      setIsReportSubmitting(false);
      return;
    }

    if (config.debug) {
      logger.log("Generuję raport systemowy:", {
        type: selectedReport,
        from: fmt(dateFrom),
        to: fmt(dateTo),
      });
    }

    downloadReport(
      selectedReport,
      fmt(dateFrom),
      fmt(dateTo),
      accessToken,
      () => setIsReportSubmitting(false)
    );
  };

  const handleDeferredGenerate = () => {
    setDeferredLoading(true);

    if (!canDeferred) {
      alert("Brak uprawnień do raportu odroczonych należności");
      setDeferredLoading(false);
      return;
    }

    let from, to;
    if (deferredPeriod.mode === "month") {
      const [y, m] = (deferredPeriod.month || "").split("-").map(Number);
      if (!y || !m) {
        alert("Wybierz miesiąc");
        setDeferredLoading(false);
        return;
      }
      from = firstDayOfMonth(y, m);
      to = lastDayOfMonth(y, m);
    } else {
      from = deferredPeriod.from;
      to = deferredPeriod.to;
      if (!from || !to) {
        alert("Podaj pełny zakres dat");
        setDeferredLoading(false);
        return;
      }
    }

    if (config.debug) {
      logger.log("Generuję raport odroczonych należności:", {
        mode: deferredPeriod.mode,
        month: deferredPeriod.month,
        from: fmt(from),
        to: fmt(to),
      });
    }

    downloadDeferredReport(fmt(from), fmt(to), accessToken, () =>
      setDeferredLoading(false)
    );
  };

  return (
    <div className="content">
      {(canBasic || canExtended) && (
        <>
          <CardHeader text={"Raport systemowy"} onlyText="true" />
          <div className="card container-fluid d-flex flex-column shadow ms-4 px-4 py-3 w55 justify-content-center">
            <div className="row p-4 gx-5">
              <div className="col-4 d-flex flex-column">
                <label className="form-label fw-medium mb-1">Typ raportu</label>

                {canBasic && (
                  <button
                    onClick={() => setSelectedReport(ReportTypeConstants.BASIC)}
                    className={`btn mb-1 ${
                      selectedReport === ReportTypeConstants.BASIC
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                  >
                    Podstawowy
                  </button>
                )}

                {canExtended && (
                  <button
                    onClick={() =>
                      setSelectedReport(ReportTypeConstants.EXTENDED)
                    }
                    className={`btn ${
                      selectedReport === ReportTypeConstants.EXTENDED
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                  >
                    Zaawansowany
                  </button>
                )}
              </div>

              <div className="col-8 d-flex flex-column justify-content-between">
                <div>
                  <label className="form-label fw-medium mb-1">
                    Podaj zakres dat:
                  </label>
                  <div className="d-flex flex-row">
                    <DatePicker
                      selected={dateFrom}
                      onChange={(date) => setDateFrom(date)}
                      selectsStart
                      startDate={dateFrom}
                      endDate={dateTo}
                      locale={"pl"}
                      placeholderText="Od"
                      dateFormat="yyyy-MM-dd"
                      className="form-control rounded-end-0 border-end-0 w5 h2"
                    />
                    <div className="form-control rounded-0 border-start-0 border-end-0 w3 px-1 h2 d-flex justify-content-center align-items-center">
                      <i className="bi bi-arrow-right"></i>
                    </div>
                    <DatePicker
                      selected={dateTo}
                      onChange={(date) => setDateTo(date)}
                      selectsEnd
                      startDate={dateFrom}
                      locale={"pl"}
                      endDate={dateTo}
                      placeholderText="Do"
                      dateFormat="yyyy-MM-dd"
                      className="form-control rounded-start-0 border-start-0 w5 h2"
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <button
                    onClick={handleGenerate}
                    className="btn btn-primary"
                    disabled={isReportSubmitting}
                  >
                    {isReportSubmitting ? "Trwa generowanie ..." : "Generuj"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {canDeferred && (
        <>
          <CardHeader
            text={"Miesięczny raport odroczonych należności"}
            onlyText="true"
          />
          <div className="card container-fluid d-flex flex-column shadow ms-4 px-4 py-3 w55 justify-content-center mb-5">
            <div className="row p-4 gx-5">
              <label className="form-label fw-medium mb-1">Okres</label>

              <div className="d-flex flex-column gap-2">
                <fieldset disabled={!canDeferred} className="border-0 p-0 m-0">
                  <PeriodSelector
                    value={deferredPeriod}
                    onChange={setDeferredPeriod}
                    monthOptions={monthOptions}
                  />

                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleDeferredGenerate}
                      disabled={deferredLoading || !canDeferred}
                    >
                      {deferredLoading ? "Generowanie..." : "Generuj"}
                    </button>
                  </div>
                </fieldset>

                {!canDeferred && (
                  <div className="text-muted small">
                    Brak uprawnienia:{" "}
                    <strong>Raport odroczonych należności</strong>.
                  </div>
                )}

                <div className="form-text">
                  Jeśli wybierzesz tryb <strong>Miesiąc</strong>, raport obejmie
                  pełny miesiąc (np. 2024-03-01 – 2024-03-31). W trybie{" "}
                  <strong>Zakres niestandardowy</strong> możesz wskazać dowolne
                  daty lub skorzystać z presetów.
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


